import { ListRow, Surface } from '@westhetics/ui'
import type { ReactNode } from 'react'
import type { Question, QuestionnaireSection } from './api'
import {
  ABOUT_YOU_NOTE,
  CONFIRMATION_NOTE,
  questionMetaLine,
  sectionEyebrow,
} from './questionnaire-labels'

/**
 * Uma pergunta do questionário em leitura, como quem edita a vê: rótulo em caixa alta com a
 * posição e o rótulo curto, a pergunta como a paciente lê em Lexend, o contexto em Lato e a linha
 * de apoio por extenso (tipo, opções, condição, descreva, atenção). Nada em Plum: ler não é risco.
 * Serve à pré-visualização da clínica, à versão publicada do root e às linhas da lista editável.
 */

export function QuestionRow({
  question,
  siblings,
  showReview,
  actions,
  className,
}: {
  question: Question
  /** As perguntas do mesmo bloco, para escrever a condição por extenso. */
  siblings: readonly Question[]
  /** Root: diz se a pergunta já tem as duas revisões (P-ANA-02). */
  showReview?: boolean
  /** Ações em texto à direita ("editar", subir e descer, "remover"). */
  actions?: ReactNode
  className?: string
}) {
  const untitled = question.label.trim() === ''
  return (
    <ListRow
      role="listitem"
      className={className}
      eyebrow={`${question.position} · ${question.shortLabel || 'sem rótulo curto'}`}
      title={untitled ? <span className="text-label">Pergunta sem texto</span> : question.label}
      meta={
        <>
          {question.context ? <div>{question.context}</div> : null}
          <div className={question.context ? 'mt-1' : undefined}>
            {questionMetaLine(question, siblings, { showReview })}
          </div>
        </>
      }
      trailing={actions}
    />
  )
}

/** As perguntas de um bloco em leitura, uma linha por pergunta. */
export function QuestionRows({
  questions,
  showReview,
  label,
  className,
}: {
  questions: readonly Question[]
  showReview?: boolean
  label: string
  className?: string
}) {
  return (
    <div role="list" aria-label={label} className={className}>
      {questions.map((question) => (
        <QuestionRow
          key={question.id}
          question={question}
          siblings={questions}
          showReview={showReview}
          className="px-0"
        />
      ))}
    </div>
  )
}

/**
 * Um bloco do questionário em leitura: o rótulo "2 · SUA SAÚDE", a introdução e as perguntas. O
 * bloco 1 não tem perguntas (são os campos do cadastro) e diz isso.
 */
export function SectionReadBlock({
  section,
  index,
  showReview,
  children,
}: {
  section: QuestionnaireSection
  index: number
  showReview?: boolean
  /** O que entra depois das perguntas (as perguntas da clínica, na tela da gestora). */
  children?: ReactNode
}) {
  const note = section.key === 'aboutYou' ? ABOUT_YOU_NOTE : section.intro
  return (
    <Surface className="max-desk:px-4" aria-labelledby={`section-${section.key}`}>
      <h2 id={`section-${section.key}`} className="eyebrow">
        {sectionEyebrow(section, index)}
      </h2>
      {note ? (
        <p className="font-body text-secondary mt-[6px] text-[12px] leading-[1.6] font-light">
          {note}
        </p>
      ) : null}
      {section.questions.length > 0 ? (
        <QuestionRows
          className="mt-3"
          label={`Perguntas do bloco ${section.name}`}
          questions={section.questions}
          showReview={showReview}
        />
      ) : section.key !== 'aboutYou' ? (
        <p className="font-body text-body text-secondary mt-3 font-light">
          Nenhuma pergunta neste bloco.
        </p>
      ) : null}
      {children}
    </Surface>
  )
}

/** O bloco 5, fixo: a declaração que a paciente aceita e a frase de prova. */
export function ConfirmationReadBlock({
  declarationText,
  index,
}: {
  declarationText: string | null
  index: number
}) {
  return (
    <Surface className="max-desk:px-4" aria-labelledby="section-confirmation">
      <h2 id="section-confirmation" className="eyebrow">
        {sectionEyebrow({ name: 'Confirmação' }, index)}
      </h2>
      <p className="font-body text-secondary mt-[6px] text-[12px] leading-[1.6] font-light">
        {CONFIRMATION_NOTE}
      </p>
      <blockquote className="border-hairline-heavy font-body text-body text-ink mt-3 border-l-2 pl-4 font-light">
        {declarationText || <span className="text-label">A declaração ainda não foi escrita.</span>}
      </blockquote>
    </Surface>
  )
}
